import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  X, User, Phone, FileText, Shield, DollarSign, Calendar, Edit, Eye, PlusCircle, ChevronLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  Active:      'bg-emerald-100 text-emerald-800 border-emerald-200',
  Lapsed:      'bg-red-100 text-red-800 border-red-200',
  'Not Renewed': 'bg-red-100 text-red-800 border-red-200',
  Matured:     'bg-blue-100 text-blue-800 border-blue-200',
  Draft:       'bg-amber-100 text-amber-800 border-amber-200',
  Cancelled:   'bg-slate-200 text-slate-700 border-slate-300',
};

// Format e.g. "Saturday, 1 August 2026" per spec.
const formatLongDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

// Two-letter avatar fallback for the client photo (uses initials).
const initialsOf = (name) => (name || 'CL').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

const PolicyDetail = () => {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [client, setClient]  = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data: p } = await axios.get(`${API}/policies/${policyId}`, { withCredentials: true });
        setPolicy(p);
        if (p?.client_id) {
          try {
            const { data: c } = await axios.get(`${API}/clients/${p.client_id}`, { withCredentials: true });
            setClient(c);
          } catch { /* client fetch is soft — page still renders without it */ }
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Policy not found');
      } finally { setLoading(false); }
    };
    load();
  }, [policyId]);

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;
  if (!policy) return (
    <div className="text-center py-16" data-testid="policy-not-found">
      <p className="text-slate-500">Policy not found or you don't have access.</p>
      <Button variant="outline" className="mt-4" onClick={() => navigate('/policies')}><ChevronLeft className="h-4 w-4 mr-1" /> Back to Products</Button>
    </div>
  );

  const statusClass = STATUS_COLORS[policy.status] || 'bg-slate-100 text-slate-700 border-slate-200';

  return (
    <div className="space-y-6" data-testid="policy-detail-page">
      {/* ---------- Header ---------- */}
      <Card className="border-slate-200 shadow-sm" data-testid="policy-detail-header">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center flex-wrap gap-2">
                <Badge className={`border ${statusClass}`} data-testid="policy-status-badge">{policy.status || 'Unknown'}</Badge>
                <Badge className="bg-slate-100 text-slate-800 border border-slate-200">{policy.product_type || '—'}</Badge>
                {policy.plan_type && <Badge variant="outline">{policy.plan_type}</Badge>}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2 tracking-tight" style={{ fontFamily: 'Manrope' }}>{policy.product_name || policy.product_type || 'Policy'}</h1>
              <p className="text-sm text-slate-500 mt-1">Policy Number · <span className="font-mono text-slate-800">{policy.policy_number || '—'}</span></p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => {
              // Prefer history.back() when the user landed here from within the app.
              // But if the previous page was the Edit dialog fork (?edit=...) or history is
              // empty (deep-link/refresh), route to the clean products list instead.
              const prev = document.referrer || '';
              if (window.history.length > 1 && !/[?&]edit=/.test(prev)) navigate(-1);
              else navigate('/policies');
            }} title="Close" data-testid="policy-detail-close" className="shrink-0">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ---------- 2-column: Client Details + Policy Info ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200 shadow-sm" data-testid="policy-client-card">
          <CardHeader className="pb-2 flex flex-row items-center gap-2"><User className="h-4 w-4 text-blue-700" /><h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Client Details</h3></CardHeader>
          <CardContent className="pt-2">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center text-lg font-bold shrink-0" data-testid="policy-client-avatar">
                {initialsOf(client?.name)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-lg font-semibold text-slate-900 truncate" data-testid="policy-client-name">{client?.name || 'Client (unknown)'}</p>
                {client?.primary_mobile && (
                  <a href={`tel:${client.primary_mobile}`} className="inline-flex items-center gap-1.5 text-sm text-blue-800 hover:underline" data-testid="policy-client-mobile">
                    <Phone className="h-3.5 w-3.5" /> {client.primary_mobile}
                  </a>
                )}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-[11px] uppercase text-slate-500">Plan Name</p><p className="font-medium text-slate-900" data-testid="policy-plan-name">{policy.product_name || '—'}</p></div>
              <div><p className="text-[11px] uppercase text-slate-500">Client Since</p><p className="font-medium text-slate-900">{client?.created_at ? new Date(client.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}</p></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="policy-info-card">
          <CardHeader className="pb-2 flex flex-row items-center gap-2"><Shield className="h-4 w-4 text-emerald-700" /><h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Policy Info</h3></CardHeader>
          <CardContent className="pt-2 grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[11px] uppercase text-slate-500">Insurer</p>
              <p className="text-base font-semibold text-slate-900" data-testid="policy-insurer">{policy.insurer || '—'}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase text-slate-500">Product Type</p>
              <p className="text-base font-semibold text-slate-900" data-testid="policy-product-type">{policy.product_type || '—'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-[11px] uppercase text-slate-500">Policy Number</p>
              <p className="font-mono text-base font-semibold text-slate-900" data-testid="policy-number">{policy.policy_number || '—'}</p>
            </div>
            {policy.nominee && (
              <div className="col-span-2"><p className="text-[11px] uppercase text-slate-500">Nominee</p><p className="text-base text-slate-900">{policy.nominee}</p></div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ---------- Financials + Policy Period ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-emerald-100 bg-emerald-50/40 shadow-sm" data-testid="policy-financials-card">
          <CardHeader className="pb-2 flex flex-row items-center gap-2"><DollarSign className="h-4 w-4 text-emerald-700" /><h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Financials</h3></CardHeader>
          <CardContent className="pt-2 grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] uppercase text-slate-500">Premium Amount</p>
              <p className="text-2xl font-bold text-emerald-800" style={{ fontFamily: 'Manrope' }} data-testid="policy-premium-amount">{formatCurrency(policy.premium_amount || 0)}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{policy.premium_frequency || 'Yearly'}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase text-slate-500">Sum Insured</p>
              <p className="text-2xl font-bold text-blue-800" style={{ fontFamily: 'Manrope' }} data-testid="policy-sum-insured">{formatCurrency(policy.sum_assured || 0)}</p>
            </div>
            {(policy.net_premium || policy.gst_amount || policy.total_premium) && (
              <div className="col-span-2 mt-2 rounded-md bg-white border border-emerald-100 p-2.5 text-xs grid grid-cols-3 gap-2" data-testid="policy-premium-breakdown">
                <div><p className="text-[10px] uppercase text-slate-500">Net</p><p className="font-semibold">{policy.net_premium ? formatCurrency(policy.net_premium) : '—'}</p></div>
                <div><p className="text-[10px] uppercase text-slate-500">GST</p><p className="font-semibold">{policy.gst_amount ? formatCurrency(policy.gst_amount) : '—'}</p></div>
                <div><p className="text-[10px] uppercase text-slate-500">Total</p><p className="font-semibold text-emerald-800">{policy.total_premium ? formatCurrency(policy.total_premium) : '—'}</p></div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-blue-100 bg-blue-50/40 shadow-sm" data-testid="policy-period-card">
          <CardHeader className="pb-2 flex flex-row items-center gap-2"><Calendar className="h-4 w-4 text-blue-700" /><h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Policy Period</h3></CardHeader>
          <CardContent className="pt-2 space-y-3">
            <div>
              <p className="text-[11px] uppercase text-slate-500">Start Date</p>
              <p className="text-base font-semibold text-slate-900" data-testid="policy-start-date">{formatLongDate(policy.start_date)}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase text-slate-500">Expiry Date</p>
              <p className="text-base font-semibold text-slate-900" data-testid="policy-end-date">{formatLongDate(policy.end_date)}</p>
            </div>
            {policy.next_due_date && (
              <div>
                <p className="text-[11px] uppercase text-slate-500">Next Due</p>
                <p className="text-sm font-medium text-blue-800" data-testid="policy-next-due">{formatLongDate(policy.next_due_date)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ---------- Footer actions ---------- */}
      <Card className="border-slate-200 shadow-sm" data-testid="policy-footer-actions">
        <CardContent className="p-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {/* Round 61 · Item 2 — removed "Add Previous Policy" button. Pure
                UI shortcut, no backend dependency; agents can still navigate
                to the client page from the breadcrumb/client link above. */}
            <Button
              variant="outline"
              onClick={() => navigate(`/policies?edit=${policy.policy_id}`)}
              data-testid="policy-action-edit"
              title="Edit this policy"
            >
              <Edit className="h-4 w-4 mr-1.5" /> Edit
            </Button>
          </div>
          <Button
            className="bg-blue-800 hover:bg-blue-900"
            onClick={() => {
              // Prefer downloadable policy PDF from the first attached document; else fall back to a per-policy PDF endpoint if present.
              const fetchDoc = async () => {
                try {
                  const { data } = await axios.get(`${API}/policies/${policy.policy_id}/documents`, { withCredentials: true });
                  const doc = (data || []).find(d => d.doc_type === 'policy') || (data || [])[0];
                  if (doc) {
                    window.open(`${API}/policy-docs/${doc.policy_doc_id}/download`, '_blank');
                  } else {
                    toast.info('No policy PDF attached yet. Attach one via Edit → Policy Document.');
                  }
                } catch (e) { toast.error(e.response?.data?.detail || 'Could not load policy documents'); }
              };
              fetchDoc();
            }}
            data-testid="policy-action-view"
            title="Open the attached policy PDF"
          >
            <Eye className="h-4 w-4 mr-1.5" /> View Policy
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PolicyDetail;
